import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from "react";
import { getRbacConfig } from "../services/adminService";
import { oauthService } from "../services/oauth";
import { ADMIN_PATHS, orderAllowedAdminPaths } from "../constants/adminPaths";

// Fallback config matching original frontend rbac.js (used while loading or on error)
const FALLBACK_ROLES = [
  "Employee",
  "Manager",
  "HR",
  "HR Admin",
  "Tech Admin",
  "CEO",
];
const FALLBACK_ROLES_WITH_ADMIN_ACCESS = [
  "Manager",
  "HR",
  "HR Admin",
  "Tech Admin",
  "CEO",
];
const FALLBACK_ADMIN_PATH_ROLES = {
  "/admin-dashboard": ["Manager", "HR", "HR Admin", "Tech Admin", "CEO"],
  "/document-manager": ["HR", "HR Admin", "Tech Admin", "CEO"],
  "/approval-workflow": ["HR", "HR Admin", "Tech Admin", "CEO"],
  "/acknowledgement-tracking": ["Manager", "HR", "HR Admin", "CEO"],
  "/rbac": ["HR Admin", "Tech Admin", "CEO"],
};

/** Legacy path kept in some DBs after document-types was renamed. */
const LEGACY_ADMIN_PATH_ALIASES = {
  "/document-types": "/approval-workflow",
};

/** Pages removed from the app. Ignore leftover rows from older configs. */
const RETIRED_ADMIN_PATHS = new Set(["/sync-management"]);

const normalizeAdminPathRoles = (adminPathRoles) => {
  if (!adminPathRoles || typeof adminPathRoles !== "object") {
    return {};
  }
  const normalized = {};
  for (const [path, roles] of Object.entries(adminPathRoles)) {
    if (!Array.isArray(roles) || RETIRED_ADMIN_PATHS.has(path)) continue;
    const targetPath = LEGACY_ADMIN_PATH_ALIASES[path] || path;
    if (RETIRED_ADMIN_PATHS.has(targetPath)) continue;
    const existing = normalized[targetPath] || [];
    normalized[targetPath] = [...new Set([...existing, ...roles])];
  }
  return normalized;
};

const mergeAdminPathRoles = (apiPathRoles) => {
  const normalizedApi = normalizeAdminPathRoles(apiPathRoles);
  const merged = { ...FALLBACK_ADMIN_PATH_ROLES };
  for (const path of ADMIN_PATHS) {
    if (Array.isArray(normalizedApi[path])) {
      merged[path] = normalizedApi[path];
    }
  }
  return merged;
};

const isCompleteAdminPathRoles = (adminPathRoles) =>
  ADMIN_PATHS.every((path) => Array.isArray(adminPathRoles?.[path]));

const applyRbacConfig = (data) => {
  const hasRoles =
    Array.isArray(data?.rolesWithAdminAccess) &&
    data.rolesWithAdminAccess.length > 0;
  const normalizedPaths = normalizeAdminPathRoles(data?.adminPathRoles);
  const hasPaths =
    normalizedPaths &&
    typeof normalizedPaths === "object" &&
    Object.keys(normalizedPaths).length > 0;

  if (!hasRoles || !hasPaths) {
    return null;
  }

  const adminPathRoles = isCompleteAdminPathRoles(normalizedPaths)
    ? normalizedPaths
    : mergeAdminPathRoles(normalizedPaths);

  return {
    ...data,
    adminPathRoles,
  };
};

const RbacConfigContext = createContext();
export { RbacConfigContext };

export const RbacConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const rolesWithAdminAccess = config?.rolesWithAdminAccess ?? FALLBACK_ROLES_WITH_ADMIN_ACCESS;
  const adminPathRoles = config?.adminPathRoles ?? FALLBACK_ADMIN_PATH_ROLES;
  const roles = config?.roles ?? FALLBACK_ROLES.map((name) => ({ name, has_admin_access: FALLBACK_ROLES_WITH_ADMIN_ACCESS.includes(name) }));

  const canAccessAdmin = (role) => {
    if (!role) return false;
    return rolesWithAdminAccess.includes(role);
  };

  const canAccessAdminPath = (role, path) => {
    if (!role) return false;
    const basePath = path.split("/").slice(0, 2).join("/") || path;
    const allowed = adminPathRoles[basePath];
    if (!allowed) return false;
    return allowed.includes(role);
  };

  const getAllowedAdminPaths = (role) => {
    if (!role) return [];
    const allowed = Object.keys(adminPathRoles).filter(
      (path) => Array.isArray(adminPathRoles[path]) && adminPathRoles[path].includes(role)
    );
    return orderAllowedAdminPaths(allowed);
  };

  const fetchConfig = useCallback(() => {
    if (!oauthService.isAuthenticated()) return Promise.resolve(null);
    return getRbacConfig().then((data) => {
      const nextConfig = applyRbacConfig(data);
      if (nextConfig) {
        setConfig(nextConfig);
      }
      return nextConfig;
    });
  }, []);

  useEffect(() => {
    if (!oauthService.isAuthenticated()) {
      setConfig(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRbacConfig()
      .then((data) => {
        if (cancelled) return;
        // Don't apply empty API response: in production reload the API can return
        // empty or partial roles/paths (e.g. DB not ready or legacy seed data),
        // which would wrongly revoke admin access. Merge with fallbacks when incomplete.
        const nextConfig = applyRbacConfig(data);
        if (nextConfig) {
          setConfig(nextConfig);
        }
        // else: leave config null so fallback roles/paths are used
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load RBAC config");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [fetchConfig]);

  const value = useMemo(
    () => ({
      config,
      roles,
      rolesWithAdminAccess,
      adminPathRoles,
      canAccessAdmin,
      canAccessAdminPath,
      getAllowedAdminPaths,
      refetch: fetchConfig,
      loading,
      error,
    }),
    [
      config,
      roles,
      rolesWithAdminAccess,
      adminPathRoles,
      loading,
      error,
    ]
  );

  return (
    <RbacConfigContext.Provider value={value}>
      {children}
    </RbacConfigContext.Provider>
  );
};

export const useRbacConfig = () => useContext(RbacConfigContext);
